/**
 * Auth API Client
 * Uses the centralized HTTP client
 * Note: Login/Register don't send auth token (skipAuth via separate instance)
 */

import { HttpClientBuilder, getHttpClient, getGatewayUrl, getApiKey } from '@ufc/core'
import type {
  AuthUser,
  LoginCredentials,
  RegisterData,
  RegisterResult,
  ForgotPasswordRequest,
  ForgotPasswordResult,
  ResetPasswordRequest,
  ResetPasswordResult,
  ChangePasswordRequest,
  ChangePasswordResult,
  ChangePasswordOtpRequest,
  ChangePasswordOtpResult,
  DeleteAccountRequest,
  DeleteAccountResult,
  UpdateProfileRequest,
  UpdateProfileResult,
} from '../../domain/types'
import {
  STRAPI_AUTH_ENDPOINTS,
  type AuthResponseDto,
  type UserDto,
  type RegisterResponseDto,
  type ForgotPasswordResponseDto,
  type ResetPasswordResponseDto,
  type ChangePasswordResponseDto,
  type RequestChangePasswordOtpResponseDto,
  type DeleteAccountResponseDto,
  type UpdateProfileResponseDto,
  validateRegisterResponse,
  validateForgotPasswordResponse,
  validateResetPasswordResponse,
  validateChangePasswordResponse,
  validateRequestChangePasswordOtpResponse,
  validateDeleteAccountResponse,
  validateUpdateProfileResponse,
} from '../../api/api-types'
import {
  transformAuthResponse,
  transformLoginCredentials,
  transformUserDtoToAuthUser,
  transformRegisterData,
  transformForgotPasswordRequest,
  transformForgotPasswordResponse,
  transformResetPasswordRequest,
  transformResetPasswordResponse,
  transformChangePasswordRequest,
  transformChangePasswordResponse,
  transformChangePasswordOtpRequest,
  transformChangePasswordOtpResponse,
  transformDeleteAccountRequest,
  transformDeleteAccountResponse,
  transformUpdateProfileRequest,
  transformUpdateProfileResponse,
} from '../../api/transformers'

// ============================================================================
// Gateway Configuration
// ============================================================================
// Note: Using getGatewayUrl() and getApiKey() from @ufc/core to ensure
// consistency with the authenticated HTTP client configuration

// ============================================================================
// Unauthenticated Client (for login/register)
// ============================================================================

let unauthenticatedClient: ReturnType<typeof HttpClientBuilder.prototype.build> | null = null

function getUnauthenticatedClient() {
  if (!unauthenticatedClient) {
    const builder = new HttpClientBuilder()
      .withBaseURL(getGatewayUrl())
      .withTimeout(30000)

    // Add Kong API key if available (required for all requests)
    const apiKey = getApiKey()
    if (apiKey) {
      builder.withHeaders({ 'X-API-Key': apiKey })
    }

    unauthenticatedClient = builder.build()
  }
  return unauthenticatedClient
}

/**
 * Reset the unauthenticated client (useful for testing or when gateway URL changes)
 */
export function resetUnauthenticatedClient(): void {
  unauthenticatedClient = null
}

function buildLocaleHeaders(locale?: string): Record<string, string> | undefined {
  const normalizedLocale = locale?.trim()
  if (!normalizedLocale) return undefined
  return { 'x-locale': normalizedLocale }
}

// ============================================================================
// Auth API
// ============================================================================

export const authApi = {
  /**
   * Login with identifier (email/username) and password
   * Does NOT send auth token (public endpoint)
   */
  async login(credentials: LoginCredentials): Promise<{ token: string; user: AuthUser }> {
    const http = getUnauthenticatedClient()
    const body = transformLoginCredentials(credentials)

    const response = await http.post<AuthResponseDto>(
      STRAPI_AUTH_ENDPOINTS.LOGIN,
      body
    )

    return transformAuthResponse(response.data)
  },

  /**
   * Register a new user
   * Does NOT send auth token (public endpoint)
   * Sends multipart/form-data (files + fields) in a single request
   */
  async register(data: RegisterData): Promise<RegisterResult> {
    const http = getUnauthenticatedClient()
    const formData = transformRegisterData(data)

    const response = await http.post<RegisterResponseDto>(
      STRAPI_AUTH_ENDPOINTS.REGISTER,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )

    const parsed = validateRegisterResponse(response.data)
    const user = transformUserDtoToAuthUser(parsed.user)
    const status = parsed.status ?? 'pending'
    const message =
      parsed.message ??
      (status === 'pending'
        ? 'Registration received. Your account will be activated before you can sign in.'
        : 'Registration successful.')

    return {
      status,
      message,
      user,
      tenant: user.tenant ?? parsed.tenant,
    }
  },

  /**
   * Get current authenticated user
   * Requires auth token
   * Populates role relation to determine admin status
   */
  async getMe(): Promise<AuthUser> {
    const http = getHttpClient()

    // Populate role relation to get user's role type
    const response = await http.get<UserDto>(STRAPI_AUTH_ENDPOINTS.ME, {
      params: {
        populate: 'role,factoryLocation,userLocation',
      },
    })

    // Debug: log raw API response
    if (import.meta.env.DEV) {
      console.log('[Auth API] getMe response:', response.data)
    }

    return transformUserDtoToAuthUser(response.data)
  },

  /**
   * Request password reset email
   * Does NOT send auth token (public endpoint)
   */
  async forgotPassword(data: ForgotPasswordRequest): Promise<ForgotPasswordResult> {
    const http = getUnauthenticatedClient()
    const body = transformForgotPasswordRequest(data)

    const response = await http.post<ForgotPasswordResponseDto>(
      STRAPI_AUTH_ENDPOINTS.FORGOT_PASSWORD, 
      body
    )

    const parsed = validateForgotPasswordResponse(response.data)
    return transformForgotPasswordResponse(parsed)
  },

  /**
   * Reset password with code
   * Does NOT send auth token (public endpoint)
   */
  async resetPassword(data: ResetPasswordRequest): Promise<ResetPasswordResult> {
    const http = getUnauthenticatedClient()
    const body = transformResetPasswordRequest(data)

    const response = await http.post<ResetPasswordResponseDto>(
      STRAPI_AUTH_ENDPOINTS.RESET_PASSWORD,
      body
    )

    const parsed = validateResetPasswordResponse(response.data)
    return transformResetPasswordResponse(parsed)
  },

  /**
   * Change password for the current authenticated user
   * Requires auth token
   */
  async changePassword(data: ChangePasswordRequest): Promise<ChangePasswordResult> {
    const http = getHttpClient()
    const body = transformChangePasswordRequest(data)

    const response = await http.post<ChangePasswordResponseDto>(
      STRAPI_AUTH_ENDPOINTS.CHANGE_PASSWORD,
      body
    )

    const parsed = validateChangePasswordResponse(response.data)
    return transformChangePasswordResponse(parsed)
  },

  /**
   * Request an OTP to verify password change for the current authenticated user
   * Requires auth token
   */
  async requestChangePasswordOtp(data: ChangePasswordOtpRequest): Promise<ChangePasswordOtpResult> {
    const http = getHttpClient()
    const body = transformChangePasswordOtpRequest(data)

    const response = await http.post<RequestChangePasswordOtpResponseDto>(
      STRAPI_AUTH_ENDPOINTS.CHANGE_PASSWORD_REQUEST_OTP,
      body,
      {
        headers: buildLocaleHeaders(data.locale),
      }
    )

    const parsed = validateRequestChangePasswordOtpResponse(response.data)
    return transformChangePasswordOtpResponse(parsed)
  },

  /**
   * Delete account for the current authenticated user
   * Requires auth token
   */
  async deleteAccount(data: DeleteAccountRequest): Promise<DeleteAccountResult> {
    const http = getHttpClient()
    const body = transformDeleteAccountRequest(data)

    const response = await http.post<DeleteAccountResponseDto>(
      STRAPI_AUTH_ENDPOINTS.DELETE_ACCOUNT,
      body
    )

    const parsed = validateDeleteAccountResponse(response.data)
    return transformDeleteAccountResponse(parsed)
  },

  /**
   * Update current authenticated user's profile fields
   * Requires auth token
   */
  async updateProfile(data: UpdateProfileRequest): Promise<UpdateProfileResult> {
    const http = getHttpClient()
    const body = transformUpdateProfileRequest(data)

    const response = await http.post<UpdateProfileResponseDto>(
      STRAPI_AUTH_ENDPOINTS.UPDATE_PROFILE,
      body
    )

    const parsed = validateUpdateProfileResponse(response.data)
    return transformUpdateProfileResponse(parsed)
  },

  /**
   * Validate stored token by calling /me endpoint
   * Returns user on success, null on auth failure (401)
   * Throws on other errors (network, 404, etc.) so caller can decide
   */
  async validateToken(): Promise<AuthUser | null> {
    try {
      return await this.getMe()
    } catch (error) {
      // Check if it's a 401 Unauthorized - token is definitely invalid
      const axiosError = error as { response?: { status?: number } }
      if (axiosError.response?.status === 401) {
        return null
      }
      // For other errors (404, network), throw so saga can handle gracefully
      throw error
    }
  },
}
